<template>
  <div class="social-reporting-health">
    <v-tooltip bottom max-width="320">
      <template #activator="{ on, attrs }">
        <v-chip
          v-bind="attrs"
          v-on="on"
          small
          :color="dailyColor"
          class="white--text social-reporting-health__badge"
          tabindex="0"
        >
          {{ dailyBadgeLabel }}
        </v-chip>
      </template>
      <span>{{ dailyTooltip }}</span>
    </v-tooltip>

    <v-tooltip v-if="showTokenStatus" bottom max-width="320">
      <template #activator="{ on, attrs }">
        <v-chip
          v-bind="attrs"
          v-on="on"
          small
          :color="tokenColor"
          class="white--text social-reporting-health__badge"
          tabindex="0"
        >
          {{ tokenBadgeLabel }}
        </v-chip>
      </template>
      <span>{{ tokenTooltip }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import axios from "@axios";
import {
  RMR_DANGER,
  RMR_INFO,
  RMR_ORANGE,
  RMR_SUCCESS,
  RMR_TEXT_SUBTLE,
} from "@/styles/tokens";

const REFRESH_MS = 5 * 60 * 1000;

export default {
  name: "SocialReportingHealth",
  data() {
    return {
      status: {},
      loading: true,
      refreshTimer: null,
    };
  },
  computed: {
    dailyTitle() {
      if (this.loading) return "Vérification de la collecte";
      const titles = {
        healthy: "Collecte quotidienne opérationnelle",
        running: "Collecte quotidienne en cours",
        paused: "Collecte quotidienne en pause",
        failed: "Échec de la collecte quotidienne",
        stale: "Collecte quotidienne en retard",
        no_success: "Aucune collecte quotidienne réussie",
        unknown: "État de la collecte indisponible",
      };
      return titles[this.status.daily_status] || titles.unknown;
    },
    dailyBadgeLabel() {
      if (this.loading) return "Collecte : vérification…";
      const labels = {
        healthy: "Collecte : OK",
        running: "Collecte : en cours",
        paused: "Collecte : en pause",
        failed: "Collecte : échec",
        stale: "Collecte : en retard",
        no_success: "Collecte : aucun succès",
        unknown: "Collecte : état inconnu",
      };
      return labels[this.status.daily_status] || labels.unknown;
    },
    dailyColor() {
      if (this.loading || this.status.daily_status === "running") {
        return RMR_INFO;
      }
      if (this.status.daily_status === "healthy") return RMR_SUCCESS;
      if (this.status.daily_status === "failed") return RMR_DANGER;
      if (this.status.daily_status === "unknown") return RMR_TEXT_SUBTLE;
      return RMR_ORANGE;
    },
    dailyText() {
      if (this.loading) return "Lecture de l’état Airflow en cours.";
      const texts = {
        healthy: "Le dernier run est réussi et récent.",
        running: "Un run est en cours ; les données les plus récentes ne sont pas encore confirmées.",
        paused: "Les données récentes et les revenus peuvent être incomplets.",
        failed: "Les données récentes et les revenus peuvent être incomplets ; consulter Airflow.",
        stale: "Aucune période récente n’a été collectée avec succès ; consulter Airflow.",
        no_success: "Aucun run réussi n’est disponible ; consulter Airflow.",
        unknown: "Impossible de confirmer l’état Airflow. Ne pas considérer les données comme à jour.",
      };
      return texts[this.status.daily_status] || texts.unknown;
    },
    dailyTooltip() {
      const lastSuccess = this.lastSuccessLabel
        ? ` Dernière période réussie : ${this.lastSuccessLabel}.`
        : "";
      return `${this.dailyTitle}. ${this.dailyText}${lastSuccess} Statut du DAG quotidien uniquement ; les rattrapages manuels ne sont pas vérifiés.`;
    },
    lastSuccessLabel() {
      const value = this.status.last_successful_interval_end;
      if (!value) return "";
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      return new Intl.DateTimeFormat("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Paris",
      }).format(parsed);
    },
    showTokenStatus() {
      return (
        this.$can("manage", "all") &&
        Boolean(this.status.token_expires_at)
      );
    },
    tokenColor() {
      if (["critical", "expired"].includes(this.status.token_expiry_level)) {
        return RMR_DANGER;
      }
      return this.status.token_expiry_level === "warning"
        ? RMR_ORANGE
        : RMR_SUCCESS;
    },
    tokenNeedsRenewal() {
      return ["warning", "critical", "expired"].includes(
        this.status.token_expiry_level
      );
    },
    tokenBadgeLabel() {
      const expiry = this.formatDate(this.status.token_expires_at);
      if (this.status.token_expiry_level === "expired") {
        return "Token Nonli : expiré";
      }
      if (this.status.token_days_to_expiry === 0) {
        return "Token Nonli : expire aujourd’hui";
      }
      return `Token Nonli : ${expiry}`;
    },
    tokenTooltip() {
      const expiry = this.formatDate(this.status.token_expires_at);
      const renewal = this.tokenNeedsRenewal
        ? " Après rotation, mettre à jour le secret et sa date d’expiration dans la configuration serveur."
        : "";
      return `Expiration configurée : ${expiry}. Cette date ne vérifie pas le token auprès de Nonli.${renewal}`;
    },
  },
  mounted() {
    this.refresh();
    this.refreshTimer = window.setInterval(this.refresh, REFRESH_MS);
  },
  beforeDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer);
  },
  methods: {
    async refresh() {
      try {
        const { data } = await axios.get("/audience/social-reporting/health");
        this.status = data || {};
      } catch (error) {
        this.status = { daily_status: "unknown" };
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      const [year, month, day] = String(value || "").split("-");
      if (!year || !month || !day) return value;
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.social-reporting-health {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  gap: $rmr-space-sm;
}

.social-reporting-health__badge {
  cursor: help;
  font-weight: 700;
}
</style>
